import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { client } from 'graphql/client';
import { createUser, deleteUserById, resetPasswordById, updateUserById } from 'graphql/mutations';
import { getUser, listUsers } from 'graphql/queries';
import { z } from 'zod';

import { errorHandler } from './errors';

import type {
    CreateUserMutationVariables,
    DeleteUserByIdMutationVariables,
    DeleteUserResult,
    ResetPasswordByIdMutationVariables,
    ResetPasswordResult,
    UpdateUserByIdMutationVariables,
    User,
} from 'API';
import { DeleteUserResultSchema, UserSchema } from 'APIzod';

const getUserQueryFn = async (id: string) => {
    try {
        const { data } = await client.graphql({ query: getUser, variables: { id } });
        return { data: UserSchema().parse(data.getUser) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while fetching user', error);
    }
};

const listUserQueryFN = async () => {
    try {
        const { data } = await client.graphql({
            query: listUsers,
        });
        return { data: z.array(UserSchema()).parse(data.listUsers) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while fetching users list', error);
    }
};

const createUserFn = async (variables: CreateUserMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: createUser, variables });
        return {
            data: data.createUser,
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while creating user', error);
    }
};

const editUserFn = async (variables: UpdateUserByIdMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: updateUserById, variables });
        return {
            data: data.updateUserById,
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while creating user', error);
    }
};

const resetPasswordFn = async (variables: ResetPasswordByIdMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: resetPasswordById, variables });
        return {
            data: data.resetPasswordById,
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while reseting user password', error);
    }
};

const deleteUserFn = async (variables: DeleteUserByIdMutationVariables) => {
    try {
        const { data } = await client.graphql({ query: deleteUserById, variables });
        return {
            data: DeleteUserResultSchema().parse(data.deleteUserById),
        };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while deleting user', error);
    }
};

export const userApi = createApi({
    reducerPath: 'users',
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    tagTypes: ['Users'],
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        createUser: builder.mutation<User, CreateUserMutationVariables>({
            queryFn: createUserFn,
            invalidatesTags: ['Users'],
        }),
        editUser: builder.mutation<User, UpdateUserByIdMutationVariables>({
            queryFn: editUserFn,
            invalidatesTags: ['Users'],
        }),
        resetPasswordById: builder.mutation<ResetPasswordResult, ResetPasswordByIdMutationVariables>({
            queryFn: resetPasswordFn,
        }),
        deleteUserById: builder.mutation<DeleteUserResult, DeleteUserByIdMutationVariables>({
            queryFn: deleteUserFn,
            invalidatesTags: ['Users'],
        }),
        getUser: builder.query<User, string>({
            queryFn: getUserQueryFn,
        }),
        getUserAttributes: builder.query({
            queryFn: async () => {
                try {
                    const userAttributes = await fetchUserAttributes();
                    return { data: userAttributes };
                } catch (error) {
                    throw errorHandler('Unexpected error occurred while fetching the user info', error);
                }
            },
        }),
        listUsers: builder.query<User[], string>({
            queryFn: listUserQueryFN,
            providesTags: ['Users'],
        }),
    }),
});

export const {
    useEditUserMutation,
    useCreateUserMutation,
    useGetUserQuery,
    useGetUserAttributesQuery,
    useListUsersQuery,
    useResetPasswordByIdMutation,
    useDeleteUserByIdMutation,
} = userApi;
